/**
 * Environment detection and URL utilities
 */

/**
 * Determines if the current environment is development
 * @returns {boolean} true if development environment
 */
export const isDevelopment = () => {
  return (
    window.location.hostname.includes('dev') || 
    window.location.hostname.includes('localhost') ||
    window.location.hostname.includes('127.0.0.1') ||
    window.location.hostname.includes('[::1]') ||
    /\.local$/.test(window.location.hostname)
  );
};

/**
 * Gets the appropriate login URL based on current domain
 * Automatically determines login URL by replacing current subdomain with "login"
 * 
 * Examples:
 * - localhost:3000 → /login (relative path)
 * - invoice.dev.lappa.org → https://login.dev.lappa.org/login
 * - invoice.lappa.org → https://login.lappa.org/login
 */
export const getLoginUrl = () => {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  
  // LOCALHOST: Use relative path to stay on same host:port
  if (hostname === 'localhost' || 
      hostname === '127.0.0.1' || 
      hostname === '[::1]' ||
      hostname.startsWith('localhost:') ||
      hostname.startsWith('127.0.0.1:')) {
    return '/login';
  }
  
  // For all other domains: replace first subdomain with "login"
  // invoice.dev.lappa.org → login.dev.lappa.org
  // invoice.lappa.org → login.lappa.org
  const domainParts = hostname.split('.');
  
  if (domainParts.length > 1) {
    // Replace first part (subdomain) with "login"
    domainParts[0] = 'login';
    const loginHostname = domainParts.join('.');
    return `${protocol}//${loginHostname}/login`;
  }
  
  // Fallback: if domain structure is unexpected, use relative path
  return '/login';
};

/**
 * Returns true if the current hostname is the login subdomain
 * (e.g. login.dev.lappa.org, login.lappa.org)
 */
export const isLoginDomain = () => {
  const hostname = window.location.hostname;
  if (!hostname || hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]') {
    return false;
  }
  const subdomain = hostname.split('.')[0];
  return subdomain === 'login';
};

/** Login / signup / forgot / activation — no session APIs (avoids 401 from a stale cookie). */
export const isPublicAuthPath = (pathname = "") => {
  if (!pathname) return false;
  return (
    pathname.includes("/login") ||
    pathname.includes("/signup") ||
    pathname.includes("/manager") ||
    pathname.startsWith("/forgot") ||
    pathname.startsWith("/activation") ||
    pathname.startsWith("/confirm-2fa") ||
    pathname.includes("/social/complete")
  );
};

export const isVerifyDomain = () => {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  if (!hostname || hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]') {
    return false;
  }
  const subdomain = hostname.split('.')[0];
  return subdomain === 'verify';
};

/**
 * Returns the "base domain" (without first subdomain) for current hostname.
 * Used so login/app redirect and cookies stay on the same product (lappa.org).
 * - login.dev.lappa.org → dev.lappa.org
 * - tax.dev.lappa.org → dev.lappa.org
 */
export const getAppBaseDomain = () => {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  if (!hostname || hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]') {
    return null;
  }
  const parts = hostname.split('.');
  if (parts.length < 2) return null;
  return parts.slice(1).join('.');
};

export const getVerifyOrigin = () => {
  if (typeof window === 'undefined') {
    return '';
  }

  const { hostname, protocol, port } = window.location;

  const isLocalDev =
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '[::1]' ||
    /\.local$/.test(hostname);

  if (isLocalDev) {
    const portPart = port ? `:${port}` : '';
    return `${protocol}//${hostname}${portPart}`;
  }

  const baseDomain = getAppBaseDomain();
  if (!baseDomain) {
    return window.location.origin;
  }

  return `${protocol}//verify.${baseDomain}`;
};

/**
 * Returns true if current host is Lappa (lappa.org). Used to keep post-login on login domain for Lappa.
 */
export const isLappaDomain = () => {
  const base = getAppBaseDomain();
  return base != null && base.toLowerCase().includes('lappa.org');
};

/**
 * Returns the full URL to redirect to after login.
 * Land on /global-settings (not `/`) so auth routes cannot bounce to /login
 * before fetchUser restores isLogged.
 * - login.dev.lappa.org → https://tax.dev.lappa.org/global-settings
 * @param {string} subdomain - e.g. 'tax', 'epr', 'invoice', 'tax-agent', 'omp'
 */
export const getPostLoginRedirectUrl = (subdomain) => {
  if (typeof window === 'undefined') return null;
  const baseDomain = getAppBaseDomain();
  if (!baseDomain || !subdomain) return null;
  const protocol = window.location.protocol;
  return `${protocol}//${subdomain}.${baseDomain}/global-settings`;
};

/**
 * Returns cookie domain for current hostname so cookies work on same product only.
 * - login.dev.lappa.org → .dev.lappa.org
 */
export const getCookieDomainFromHostname = () => {
  const base = getAppBaseDomain();
  return base ? `.${base}` : null;
};

/**
 * Subdomain for each app_id (for building project URLs on current domain).
 */
const SUBDOMAIN_BY_APP_ID = {
  1: 'tax',
  2: 'epr',
  3: 'invoice',
  4: 'tax-agent',
  5: 'omp',
  6: 'authorize',
};

/**
 * Returns the URL to use when switching to another project (L'Tax, L'EPR, L'Invoice, L'Authorize).
 * Go to app subdomain on current product domain.
 * - tax.dev.lappa.org, epr.dev.lappa.org, invoice.dev.lappa.org, authorize.dev.lappa.org
 * - Localhost: returns null (caller should use project.href from config).
 * @param {number} appId - app_id (1=tax, 2=epr, 3=invoice, 4=tax-agent, 5=omp, 6=authorize)
 * @returns {string|null} full URL or null for localhost
 */
export const getProjectSwitchUrl = (appId) => {
  if (typeof window === 'undefined') return null;
  const baseDomain = getAppBaseDomain();
  if (!baseDomain) return null;
  const subdomain = SUBDOMAIN_BY_APP_ID[Number(appId)];
  if (!subdomain) return null;
  const protocol = window.location.protocol;
  return `${protocol}//${subdomain}.${baseDomain}/`;
};

/**
 * Returns true if redirectUrl belongs to the same "product" as current origin (same base domain).
 * Used to avoid redirecting to a different product domain.
 */
export const isRedirectUrlSameProduct = (redirectUrl) => {
  if (!redirectUrl || typeof redirectUrl !== 'string') return false;
  try {
    const currentBase = getAppBaseDomain();
    if (!currentBase) return false;
    const parsed = new URL(redirectUrl, window.location.origin);
    const parts = parsed.hostname.split('.');
    if (parts.length < 2) return false;
    const redirectBase = parts.slice(1).join('.');
    return redirectBase.toLowerCase() === currentBase.toLowerCase();
  } catch {
    return false;
  }
};

/**
 * Gets the base login URL (without /login) for redirects
 * Automatically determines base URL by replacing current subdomain with "login"
 * 
 * Examples:
 * - localhost:3000 → http://localhost:3000
 * - invoice.dev.lappa.org → https://login.dev.lappa.org
 * - invoice.lappa.org → https://login.lappa.org
 */
export const getLoginBaseUrl = () => {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  const port = window.location.port;
  
  // LOCALHOST: Return current host with protocol
  if (hostname === 'localhost' || 
      hostname === '127.0.0.1' || 
      hostname === '[::1]' ||
      hostname.startsWith('localhost:') ||
      hostname.startsWith('127.0.0.1:')) {
    const host = hostname.split(':')[0];
    const portPart = port ? `:${port}` : '';
    return `${protocol}//${host}${portPart}`;
  }
  
  // For all other domains: replace first subdomain with "login"
  const domainParts = hostname.split('.');
  
  if (domainParts.length > 1) {
    domainParts[0] = 'login';
    const loginHostname = domainParts.join('.');
    return `${protocol}//${loginHostname}`;
  }
  
  // Fallback: return current origin
  return `${protocol}//${hostname}${port ? `:${port}` : ''}`;
};

/**
 * Gets the API URL from environment variable
 * @returns {string} API URL from REACT_APP_API_URL
 */
export const getApiUrl = () => {
  return process.env.REACT_APP_API_URL || "";
};

export const getReferralSignupBaseUrl = () => {
  if (typeof window === 'undefined') {
    return getLoginBaseUrl().replace(/\/$/, '');
  }

  const hostname = window.location.hostname;
  const isLocalDev =
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '[::1]' ||
    /\.local$/.test(hostname);

  if (isLocalDev) {
    return window.location.origin.replace(/\/$/, '');
  }

  return getLoginBaseUrl().replace(/\/$/, '');
};

/**
 * Full URL of the current page. Use as return_url when redirecting to payment (Stripe etc.)
 * so that after payment the user is returned to the same domain (lappa.org).
 */
export const getPaymentReturnUrl = () => {
  if (typeof window === 'undefined') return '';
  return window.location.origin + window.location.pathname + window.location.search;
};
